import { MFA_UI_CONFIG } from '@constants'

/** Digits in a US/Canada display we treat as “10-digit” (area + exchange + last 3). */
const PHONE_NANP_DISPLAY_DIGIT_COUNT = 10

/**
 * Masks an email for MFA “code sent to …” copy.
 * - Single-character local parts are left visible.
 * - Short locals (2–{@link MFA_UI_CONFIG.EMAIL_LOCAL_SHORT_MAX_LEN} chars): first character + asterisks for the rest.
 * - Longer locals: leading asterisks + last {@link MFA_UI_CONFIG.EMAIL_MASK_TAIL_VISIBLE} characters of the local part (same tail-reveal idea as {@link maskPhoneForMfaDisplay}).
 * If the local part already contains `*`, the email is returned unchanged.
 * Okta “h...a@domain” style locals are converted to h***a@domain for consistent obfuscation.
 */
export function maskEmailForMfaDisplay(raw: string | null | undefined): string {
  if (raw == null || raw === '') return ''
  const trimmed = String(raw).trim()
  const at = trimmed.indexOf('@')
  if (at <= 0) return trimmed

  const localPart = trimmed.slice(0, at)
  const domain = trimmed.slice(at + 1)
  if (!domain) return trimmed

  if (localPart.includes('*')) return trimmed

  // Okta email-factor responses often use a single-char elided local, e.g. h...a (first + 3+ dots + last).
  // Map to the same asterisk-style “code sent to” display as the rest of MFA, not raw dots.
  const oktaElidedLocal = /^(.)\.{3,}(.)$/.exec(localPart)
  if (oktaElidedLocal) {
    const [, first, last] = oktaElidedLocal
    return `${first}***${last}@${domain}`
  }

  if (localPart.length <= 1) {
    return `${localPart}@${domain}`
  }

  const shortMax = MFA_UI_CONFIG.EMAIL_LOCAL_SHORT_MAX_LEN
  if (localPart.length <= shortMax) {
    const maskedLocal = `${localPart[0]}${'*'.repeat(localPart.length - 1)}`
    return `${maskedLocal}@${domain}`
  }

  const lead = MFA_UI_CONFIG.EMAIL_MASK_LEADING_ASTERISKS
  const tailN = MFA_UI_CONFIG.EMAIL_MASK_TAIL_VISIBLE
  const tail = localPart.slice(-tailN)
  return `${'*'.repeat(lead)}${tail}@${domain}`
}

/**
 * Masks a phone string for MFA “code sent to …” copy: only `*` and the last 3 digits (Figma/registration style).
 * - Unmasked (no `*`, `X`, `x`): all digits are stripped; last {@link MFA_UI_CONFIG.PHONE_LAST_DIGITS_COUNT}
 *   stay visible.
 * - Already server-masked (`*`, or `X` for Okta): we do not concatenate all digits (that mangles `+1` with the
 *   number). We take the last “significant” digit run (e.g. `4250` from `+1 XXX-XXX-4250`), use its last 3, and
 *   prefix the {@link PHONE_NANP_DISPLAY_DIGIT_COUNT}-digit display width with stars so login matches
 *   `*******250` the same as registration, not `+1 ***-***-4250` or `**250`. Short *-only tails (e.g. `**538`)
 *   get the same 7+3 width.
 */
export function maskPhoneForMfaDisplay(raw: string | null | undefined): string {
  if (raw == null || raw === '') return ''
  const trimmed = String(raw).trim()
  const lastN = MFA_UI_CONFIG.PHONE_LAST_DIGITS_COUNT

  const isServerPreMasked = /[*Xx]/.test(trimmed)
  if (isServerPreMasked) {
    const runs = trimmed.match(/\d+/g) ?? []
    if (runs.length === 0) {
      return trimmed.replace(/[Xx]/g, '*')
    }
    // Rightmost run with at least 3 digits (e.g. `4250` after `+1 …`), not the leading `1` from `+1`
    const lastRun = [...runs].reverse().find((r) => r.length >= lastN) ?? runs[runs.length - 1] ?? ''
    if (lastRun.length < lastN) {
      return trimmed.replace(/[Xx]/g, '*')
    }
    const lastDigits = lastRun.slice(-lastN)
    const starCount = Math.max(0, PHONE_NANP_DISPLAY_DIGIT_COUNT - lastN)
    return `${'*'.repeat(starCount)}${lastDigits}`
  }

  const digits = trimmed.replace(/\D/g, '')
  if (digits.length < MFA_UI_CONFIG.PHONE_MASK_MIN_LENGTH) return trimmed

  const lastDigits = digits.slice(-lastN)
  const asteriskCount = Math.max(digits.length - lastN, 0)

  return `${'*'.repeat(asteriskCount)}${lastDigits}`
}
